import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, PlusIcon, ClockIcon, UserIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';
import { format } from 'date-fns';

const TasksWidget = () => {
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState('all'); // all, assigned_to_me, created_by_me

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const loadInitialData = async () => {
    try {
      const [statusesRes, prioritiesRes, usersRes] = await Promise.all([
        api.get('/portal/tasks/statuses'),
        api.get('/portal/tasks/priorities'),
        api.get('/user-management?limit=9999')
      ]);

      setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : []);
      setPriorities(Array.isArray(prioritiesRes.data) ? prioritiesRes.data : []);
      setUsers(Array.isArray(usersRes.data.users) ? usersRes.data.users : (Array.isArray(usersRes.data) ? usersRes.data : []));
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      let params = '';
      if (filter === 'assigned_to_me') {
        params = '?assigned_to_me=true';
      } else if (filter === 'created_by_me') {
        params = '?created_by_me=true';
      }

      const response = await api.get(`/portal/tasks${params}`);
      setTasks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatusId) => {
    try {
      await api.put(`/portal/tasks/${taskId}`, { status_id: newStatusId });
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task status');
    }
  };

  const getStatusColor = (statusColor) => {
    const colorMap = {
      gray: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800'
    };
    return colorMap[statusColor] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priorityColor) => {
    const colorMap = {
      gray: 'border-l-4 border-gray-300',
      blue: 'border-l-4 border-blue-500',
      orange: 'border-l-4 border-orange-500',
      red: 'border-l-4 border-red-500'
    };
    return colorMap[priorityColor] || 'border-l-4 border-blue-500';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const isCompleted = (task) => {
    return task.is_completed_state || (task.status_name && task.status_name.toLowerCase() === 'completed');
  };

  if (loading) {
    return (
      <div className="portal-widget h-full">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-widget h-full flex flex-col">
      <div className="portal-widget-header flex items-center justify-between">
        <h3 className="portal-widget-title flex items-center">
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          Tasks
        </h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary px-3 py-1 text-sm flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          New
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-2 mb-4 overflow-x-auto">
        {[
          { value: 'all', label: 'All Tasks' },
          { value: 'assigned_to_me', label: 'Assigned to Me' },
          { value: 'created_by_me', label: 'Created by Me' }
        ].map((filterOption) => (
          <button
            key={filterOption.value}
            onClick={() => setFilter(filterOption.value)}
            className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
              filter === filterOption.value
                ? 'bg-blue-600 text-white'
                : 'theme-bg-secondary theme-text-secondary hover:bg-blue-100'
            }`}
          >
            {filterOption.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 max-h-96">
        {tasks.length === 0 ? (
          <div className="text-center py-8 theme-text-secondary">
            <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No tasks found</p>
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              className={`theme-bg-secondary rounded-lg p-4 ${getPriorityColor(task.priority_color)} hover:shadow-md transition-shadow cursor-pointer`}
              onClick={() => setEditingTask(task)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-1">
                    <h4 className={`font-medium theme-text-primary ${isCompleted(task) ? 'line-through opacity-60' : ''}`}>
                      {task.title}
                    </h4>
                    {task.description && (
                      <p className="theme-text-secondary text-sm mt-1 line-clamp-2">
                        {task.description}
                      </p>
                    )}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(task.status_color)}`}>
                  {task.status_name}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs theme-text-tertiary mt-3">
                <div className="flex items-center space-x-4">
                  {task.assigned_to && task.assignee_name && (
                    <span className="flex items-center">
                      <UserIcon className="h-3 w-3 mr-1" />
                      {task.assignee_name}
                    </span>
                  )}
                  {task.priority_name && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      task.priority_color === 'red' ? 'bg-red-100 text-red-800' :
                      task.priority_color === 'orange' ? 'bg-orange-100 text-orange-800' :
                      task.priority_color === 'blue' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.priority_name}
                    </span>
                  )}
                  {task.comment_count > 0 && (
                    <span>💬 {task.comment_count}</span>
                  )}
                </div>
                {task.due_date && (
                  <span className={`flex items-center ${isOverdue(task.due_date) && !isCompleted(task) ? 'text-red-600 font-semibold' : ''}`}>
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {format(new Date(task.due_date), 'MMM dd')}
                    {isOverdue(task.due_date) && !isCompleted(task) && ' (Overdue)'}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <TaskModal
          onClose={() => setShowCreateModal(false)}
          onSaved={fetchTasks}
          statuses={statuses}
          priorities={priorities}
          users={users}
        />
      )}

      {editingTask && (
        <TaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSaved={fetchTasks}
          statuses={statuses}
          priorities={priorities}
          users={users}
        />
      )}
    </div>
  );
};

// Task Modal (Create/Edit)
const TaskModal = ({ task, onClose, onSaved, statuses, priorities, users }) => {
  const isEditing = !!task;

  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    assigned_to: task?.assigned_to || '',
    status_id: task?.status_id || (statuses.find(s => s.is_default)?.id || ''),
    priority_id: task?.priority_id || (priorities.find(p => p.level === 2)?.id || ''),
    due_date: task?.due_date ? task.due_date.split('T')[0] : '',
    start_date: task?.start_date ? task.start_date.split('T')[0] : '',
    progress_percentage: task?.progress_percentage || 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        assigned_to: formData.assigned_to || null,
        status_id: parseInt(formData.status_id),
        priority_id: parseInt(formData.priority_id),
        progress_percentage: parseInt(formData.progress_percentage)
      };

      if (isEditing) {
        await api.put(`/portal/tasks/${task.id}`, dataToSend);
      } else {
        await api.post('/portal/tasks', dataToSend);
      }

      onSaved();
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await api.delete(`/portal/tasks/${task.id}`);
      onSaved();
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-content max-w-3xl">
          <div className="modal-header">
            <h2 className="text-xl font-semibold theme-text-primary">
              {isEditing ? 'Edit Task' : 'Create Task'}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="form-input"
                  placeholder="Enter task title"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-textarea"
                  placeholder="Enter task description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label>Assign To</label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Unassigned</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name} (${user.email})`
                          : user.email
                        }
                      </option>
                    ))}
                  </select>
                  <p className="text-xs theme-text-tertiary mt-1">
                    💡 Assignee will receive email and in-app notification
                  </p>
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <select
                    required
                    value={formData.status_id}
                    onChange={(e) => setFormData({ ...formData, status_id: e.target.value })}
                    className="form-select"
                  >
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    required
                    value={formData.priority_id}
                    onChange={(e) => setFormData({ ...formData, priority_id: e.target.value })}
                    className="form-select"
                  >
                    {priorities.map((priority) => (
                      <option key={priority.id} value={priority.id}>
                        {priority.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Progress</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={formData.progress_percentage}
                      onChange={(e) => setFormData({ ...formData, progress_percentage: e.target.value })}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium theme-text-primary w-12 text-right">
                      {formData.progress_percentage}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              {isEditing && task && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="grid grid-cols-2 gap-2 theme-text-secondary">
                    {task.creator_name && (
                      <div>
                        <strong>Created by:</strong> {task.creator_name}
                      </div>
                    )}
                    {task.created_at && (
                      <div>
                        <strong>Created:</strong> {format(new Date(task.created_at), 'MMM dd, yyyy')}
                      </div>
                    )}
                    {task.assigner_name && (
                      <div>
                        <strong>Assigned by:</strong> {task.assigner_name}
                      </div>
                    )}
                    {task.comment_count > 0 && (
                      <div>
                        <strong>Comments:</strong> {task.comment_count}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer flex justify-between">
              <div>
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="btn-secondary text-red-600 hover:bg-red-50"
                    disabled={saving}
                  >
                    Delete Task
                  </button>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (isEditing ? 'Update Task' : 'Create Task')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TasksWidget;
